import { useQuery } from '@tanstack/react-query';
import ms from 'ms';

import { toast } from '@hivebrite/design-system/Toast';

import { QUERY_KEYS } from 'hb-react/backoffice/components/CategoriesList/constants';
import { getCategories } from 'hb-react/backoffice/components/CategoriesList/requests';
import type {
  CategoriesResponse,
  ConfigUrls,
} from 'hb-react/backoffice/components/CategoriesList/types';
import { makeConfigUrls } from 'hb-react/backoffice/components/CategoriesList/utils/configUrls';
import type { Network } from 'hb-react/types';

interface GetCategoriesProps {
  configUrls: Pick<ConfigUrls, 'getCategoriesPath'>;
  type: string;
  topicId?: number;
  networkId: Network['id'];
  page?: number;
  perPage?: number;
  hasPagination?: boolean;
  searchKeyword?: string;
}

export const useGetCategories = ({
  configUrls,
  hasPagination = false,
  networkId,
  page,
  perPage,
  searchKeyword,
  topicId,
  type,
}: GetCategoriesProps) => {
  const effectiveConfigUrls = hasPagination
    ? makeConfigUrls({
        networkId,
        type,
        topicId,
        page,
        perPage,
        hasPagination,
        searchKeyword,
      })
    : configUrls;

  const { data, isLoading } = useQuery<CategoriesResponse>({
    queryKey: [
      QUERY_KEYS.categories,
      type,
      topicId,
      networkId,
      page,
      searchKeyword,
    ],
    queryFn: async () => {
      try {
        return await getCategories(effectiveConfigUrls);
      } catch (error) {
        toast.error(__('Sorry, an error occurred'));
        throw error;
      }
    },
    staleTime: ms('5m'),
  });

  const categories = data?.data || [];
  const total = data?.total || null;

  return {
    categories,
    isLoading,
    totalCategories: total,
  };
};
